import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  NgClass,
  NgFor,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgTemplateOutlet,
} from '@angular/common';
import { map, Observable, skip, Subject, switchMap, takeUntil } from 'rxjs';

import { ComparisonService } from '../../../services/comparison.service';
import { UserAccessService } from '../../../../../user/access/user-access.service';
import { formatLayerName } from '../../../../common-colorscale/layer-name-format.pipe';
import { formatNumbers } from '../../../../../shared/pipes/number-formatter.pipe';
import { SubscriptionPlans } from '../../../../../user/user/user.model';
import {
  COUNTY_LEVEL_LAYER,
  H3_RES5_LEVEL_LAYER,
  H3_RES7_LEVEL_LAYER,
} from '../../../services/layer-store.service';
import {
  LAND_AREA_FRACTION,
  POPULATION,
} from '../../../../../shared/types/feature-data-type';
import {
  COUNTY_ONLY_FEATURES,
  COUNTY_ONLY_UNAVAILABLE_NOTE,
  GLANCE_LABELS,
  GLANCE_TILES,
  HERO_SCORE,
  HERO_SUB_INDICES,
  OVERVIEW_TAB_ID,
  POPUP_TABS,
  PopupIcon,
  PopupTab,
} from '../popup-groups';
import {
  hasNationalComparison,
  NationalDeviation,
  nationalDeviation,
  nationalValueOf,
} from '../national-comparison';
import { hwwGrade, HwwGrade } from '../hww-grade';

export interface MetricRow {
  feature: string;
  label: string;
  value: string;
  locked: boolean;
  deviation: NationalDeviation | null;
  /** `US avg 78,538` — the reference the bar is drawn against. */
  reference: string;
  /** Long-form comparison, shown on hover and read by screen readers. */
  note: string;
}

export interface GlanceTile extends MetricRow {
  icon: PopupIcon;
  caption: string;
}

export interface SubIndexRow {
  label: string;
  grade: HwwGrade | null;
  locked: boolean;
}

export interface RenderSection {
  label: string | null;
  note: string | null;
  rows: MetricRow[];
}

export interface RenderTab {
  id: string;
  label: string;
  icon: PopupIcon;
  sections: RenderSection[];
  lockedCount: number;
}

/** Short names for the hero meters. The full names live in the tooltips. */
const SUB_INDEX_LABELS = ['Health', 'Income', 'Education'];

/** How many metrics the overview calls out as unusual for this place. */
const STANDOUT_COUNT = 4;

/**
 * The body of the location popup: a national grade, then four subject tabs.
 *
 * Replaces the flat list this popup used to show (see LevelsPopupListComponent,
 * still used for the hover popup, where one value is the whole point). Four
 * things changed and each is load-bearing:
 *
 *  - Every row is drawn against the national value the ComparisonService
 *    already caches, so the comparison stops being a sentence at the end of a
 *    line and becomes the row's shape.
 *  - Subjects are tabs rather than one long scroll, so the card opens at a
 *    readable height on a phone and each panel is a short, complete list.
 *  - The overview names the handful of metrics furthest from the US average,
 *    which is the analysis a reader would otherwise have to do by scrolling.
 *  - Features the visitor's plan does not cover are *rendered locked* rather
 *    than filtered out. Hiding them meant the strongest upgrade argument this
 *    product has — the data sitting one click away — was invisible.
 */
@Component({
  selector: 'app-grouped-popup-list',
  templateUrl: './grouped-popup-list.component.html',
  styleUrls: ['./grouped-popup-list.component.scss'],
  imports: [NgIf, NgFor, NgClass, NgSwitch, NgSwitchCase, NgTemplateOutlet],
})
export class GroupedPopupListComponent implements OnChanges, OnDestroy {
  @Input() properties!: { [key: string]: any };
  @Input() title!: string;
  @Input() activeLayer!: string;
  @Input() activeLevel!: string;
  /** Drawn instead of `N/A` while the colourscale for `activeLayer` loads. */
  @Input() isLoading = false;

  /**
   * A locked metric the visitor asked to see. The shell owns the upsell modal
   * and the analytics that go with it: this component only knows what was
   * clicked, which keeps it free of the modal/GA dependency graph.
   */
  @Output() unlockRequested = new EventEmitter<{
    feature: string;
    label: string;
  }>();

  public heroTitle = '';
  public heroGrade: HwwGrade | null = null;
  public heroLocked = false;
  public subIndices: SubIndexRow[] = [];

  public activeRow: MetricRow | null = null;
  public glance: GlanceTile[] = [];
  public standouts: MetricRow[] = [];
  public tabs: RenderTab[] = [];

  public activeTabId = OVERVIEW_TAB_ID;

  private comparisonNotes: { [feature: string]: string } = {};
  private lookup = new Map<string, any>();
  private readonly destroy$ = new Subject<void>();
  private readonly properties$ = new Subject<{ [key: string]: any }>();

  constructor(
    private comparisonService: ComparisonService,
    private accessService: UserAccessService
  ) {
    // `switchMap`, because the shell fills `properties` in more than once per
    // popup (MapPopupComponent's written-in-state and area-score loads both
    // mutate it). Re-subscribing per change left every one of those requests in
    // flight, each having read the same empty localStorage and each issuing the
    // same fetch; only the last answer is wanted, and cancelling the rest is
    // what stops the duplicates.
    this.properties$
      .pipe(
        switchMap((properties) => this.loadComparison(properties)),
        takeUntil(this.destroy$)
      )
      .subscribe((notes) => {
        this.comparisonNotes = notes;
        // National values only reach localStorage here, so the deviation bars
        // are drawn on this second pass rather than the first render.
        this.build();
      });

    // What the visitor may see is read once, at build time, so signing in
    // while this popup is open otherwise left every padlock - the overall
    // grade included - exactly as it was until the page was reloaded. The
    // numbers themselves are already in `properties` (the features endpoint
    // answers anonymous callers too), so re-running the build is the whole
    // fix. `skip(1)` drops the BehaviorSubject's current value: ngOnChanges
    // builds the first render.
    this.accessService.accessStatus
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.build());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['properties']) {
      this.indexProperties();
      this.properties$.next(this.properties);
    }

    this.build();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public selectTab(id: string): void {
    this.activeTabId = id;
  }

  public get activeTab(): RenderTab | null {
    return this.tabs.find((tab) => tab.id === this.activeTabId) ?? null;
  }

  public get isOverview(): boolean {
    return this.activeTabId === OVERVIEW_TAB_ID;
  }

  /** A locked row is the whole point of showing it, so the row is the trigger. */
  public handleLockedClick(row: MetricRow): void {
    if (!row.locked) return;
    this.unlockRequested.emit({ feature: row.feature, label: row.label });
  }

  public handleHeroLockedClick(): void {
    this.unlockRequested.emit({ feature: HERO_SCORE, label: this.heroTitle });
  }

  /** Water cells carry the administrative area's data, which skews everything. */
  public get isMostlyWater(): boolean {
    const fraction = this.lookup.get(LAND_AREA_FRACTION);
    return (
      typeof fraction === 'number' &&
      fraction < 0.75 &&
      (this.activeLevel === H3_RES5_LEVEL_LAYER ||
        this.activeLevel === H3_RES7_LEVEL_LAYER)
    );
  }

  /**
   * Set when the map is coloured by a county-only layer at a finer level and
   * has nothing to show for it, so the empty pinned row says why instead of
   * leaving a bare `N/A`.
   *
   * Gated on the row actually being empty: a value and "published only at the
   * county level" side by side would contradict each other.
   */
  public get activeLayerLevelNote(): string | null {
    if (this.activeRow?.value !== 'N/A') return null;

    const isCountyOnly = COUNTY_ONLY_FEATURES.some(
      (feature) => feature.toUpperCase() === this.activeLayer?.toUpperCase()
    );

    return isCountyOnly && this.activeLevel !== COUNTY_LEVEL_LAYER
      ? COUNTY_ONLY_UNAVAILABLE_NOTE
      : null;
  }

  public get isTinyPopulation(): boolean {
    const population = this.lookup.get(POPULATION.toUpperCase());
    return typeof population === 'number' && population < 10;
  }

  /**
   * Bar geometry. The track is the full row and the centre tick is the
   * national value, so a fill of `n%` reaches `n/50` of the way to saturation
   * on whichever side of the country the cell sits.
   *
   * The 2% floor keeps a real-but-small gap visible: without it every metric
   * within a few percent of the country renders as an empty track, which reads
   * as missing data rather than as a close match.
   */
  public fillWidth(deviation: NationalDeviation): number {
    if (deviation.offset === 0) return 0;
    return Math.max(Math.abs(deviation.offset) * 50, 2);
  }

  public fillOffset(deviation: NationalDeviation): number {
    return deviation.offset >= 0 ? 50 : 50 - this.fillWidth(deviation);
  }

  public trackByFeature(_index: number, row: MetricRow): string {
    return row.feature;
  }

  public trackById(_index: number, tab: RenderTab): string {
    return tab.id;
  }

  public trackByIndex(index: number): number {
    return index;
  }

  private indexProperties(): void {
    this.lookup.clear();
    for (const key in this.properties ?? {}) {
      // Tile server properties arrive lowercase, backend ones uppercase, and
      // the same metric can come from either depending on the zoom level.
      this.lookup.set(key.toUpperCase(), this.properties[key]);
    }
  }

  /** Comparison notes keyed the way `buildRow` looks them up. */
  private loadComparison(properties: {
    [key: string]: any;
  }): Observable<{ [feature: string]: string }> {
    return this.comparisonService
      .getAndCalculateComparisonData(properties)
      .pipe(
        map((notes) => {
          const byFeature: { [feature: string]: string } = {};
          for (const key in notes ?? {}) {
            if (notes![key]) byFeature[key.toUpperCase()] = notes![key];
          }
          return byFeature;
        })
      );
  }

  private build(): void {
    const plan = this.accessService.getUserPlan();

    this.buildHero(plan);

    this.activeRow = this.activeLayer
      ? this.buildRow(this.activeLayer, plan, true)
      : null;

    this.tabs = POPUP_TABS.map((tab) => this.buildTab(tab, plan)).filter(
      (tab) => tab.sections.some((section) => section.rows.length > 0)
    );

    this.glance = GLANCE_TILES.map(({ feature, icon }) => {
      const row = this.buildRow(feature, plan, false);
      return row
        ? { ...row, icon, caption: GLANCE_LABELS[feature] ?? row.label }
        : null;
    }).filter((tile): tile is GlanceTile => tile !== null);

    this.standouts = this.pickStandouts();
  }

  /**
   * The metrics furthest from the national value, largest gap first.
   *
   * Ranked on the bar's own `offset` rather than the raw percentage, so a
   * ratio metric at 3x the country and a trend 2 points above it can be
   * compared on one scale. Locked rows are excluded — a visitor cannot act on
   * a highlight whose number is hidden — and so is whatever the map is already
   * coloured by, which is pinned directly above.
   */
  private pickStandouts(): MetricRow[] {
    const seen = new Set<string>([this.activeLayer?.toUpperCase()]);
    const excluded = new Set(
      POPUP_TABS.flatMap((tab) =>
        tab.sections
          .filter((section) => section.excludeFromHighlights)
          .flatMap((section) => section.features.map((f) => f.toUpperCase()))
      )
    );
    const candidates: MetricRow[] = [];

    for (const tab of this.tabs) {
      for (const section of tab.sections) {
        for (const row of section.rows) {
          const key = row.feature.toUpperCase();
          if (row.locked || !row.deviation || seen.has(key)) continue;
          if (excluded.has(key)) continue;
          seen.add(key);
          candidates.push(row);
        }
      }
    }

    return candidates
      .sort(
        (a, b) => Math.abs(b.deviation!.offset) - Math.abs(a.deviation!.offset)
      )
      .slice(0, STANDOUT_COUNT);
  }

  private buildHero(plan: SubscriptionPlans | null): void {
    // "Neighborhood Score by Country" — the scope is spelled out in the
    // caption underneath, so it would only be said twice here.
    this.heroTitle = formatLayerName(HERO_SCORE, plan)
      .replace(/\s+by\s+Country$/i, '')
      .trim();

    this.heroLocked = !this.accessService.userHasAccess(HERO_SCORE);
    this.heroGrade = this.heroLocked
      ? null
      : hwwGrade(HERO_SCORE, this.lookup.get(HERO_SCORE.toUpperCase()));

    this.subIndices = HERO_SUB_INDICES.map((feature, index) => {
      const locked = !this.accessService.userHasAccess(feature);
      return {
        label: SUB_INDEX_LABELS[index],
        locked,
        grade: locked
          ? null
          : hwwGrade(feature, this.lookup.get(feature.toUpperCase())),
      };
    });
  }

  private buildTab(tab: PopupTab, plan: SubscriptionPlans | null): RenderTab {
    const sections = tab.sections
      .filter(
        (section) =>
          !section.levels || section.levels.includes(this.activeLevel)
      )
      .map((section) => ({
        label: section.label,
        note: section.note ?? null,
        rows: section.features
          .map((feature) => this.buildRow(feature, plan, false))
          .filter((row): row is MetricRow => row !== null),
      }))
      .filter((section) => section.rows.length > 0);

    const lockedCount = sections.reduce(
      (total, section) =>
        total + section.rows.filter((row) => row.locked).length,
      0
    );

    return {
      id: tab.id,
      label: tab.label,
      icon: tab.icon,
      sections,
      lockedCount,
    };
  }

  /**
   * @param keepEmpty the active layer keeps its row even with nothing in it,
   *   because "N/A here" is the answer to the question the visitor just asked
   *   by clicking the cell. Everywhere else an empty row is noise.
   */
  private buildRow(
    feature: string,
    plan: SubscriptionPlans | null,
    keepEmpty: boolean
  ): MetricRow | null {
    const name = formatLayerName(feature, plan);
    if (!name) return null;

    const locked = !this.accessService.userHasAccess(feature);
    const raw = this.lookup.get(feature.toUpperCase());
    const { label, prefix, suffix } = splitUnit(name);

    if (locked) {
      return {
        feature,
        label,
        value: '',
        locked,
        deviation: null,
        reference: '',
        note: '',
      };
    }

    const isEmpty = raw == null || raw === '';
    if (isEmpty && !keepEmpty) return null;

    const formatted = isEmpty ? '' : formatNumbers(raw, 2, feature, true);
    if (formatted === '' && !keepEmpty) return null;

    // An extensive quantity or one whose country figure is not the same
    // measure has no honest reference, so neither the caption nor the bar is
    // drawn — see NO_COMPARISON_EXACT in national-comparison.ts.
    const national = hasNationalComparison(feature)
      ? nationalValueOf(feature)
      : null;
    // The reference goes through the *same* formatter as the cell's own value
    // above. Anything else and the two numbers in the row are not comparable —
    // an earlier pre-scaling step here turned a national obesity rate of 0.32
    // into "3200%", because formatNumbers already multiplies these fractions
    // by 100 on its own.
    const reference =
      national == null
        ? ''
        : `US avg ${prefix}${formatNumbers(national, 2, feature, true)}${suffix}`;

    return {
      feature,
      label,
      value: formatted === '' ? 'N/A' : `${prefix}${formatted}${suffix}`,
      locked: false,
      deviation:
        typeof raw === 'number'
          ? nationalDeviation(feature, raw, national)
          : null,
      reference,
      note: this.comparisonNotes[feature.toUpperCase()] ?? reference,
    };
  }
}

/**
 * `formatLayerName` returns display names with the unit tacked on after a
 * comma ("Median household income, $"). In a two-column row the unit belongs
 * with the number, not the label — but only the two units that read as an
 * affix. "offenses per 100K ppl" is a phrase and stays where it is.
 */
function splitUnit(name: string): {
  label: string;
  prefix: string;
  suffix: string;
} {
  if (name.endsWith(', $')) {
    return { label: name.slice(0, -3), prefix: '$', suffix: '' };
  }
  if (name.endsWith(', %')) {
    return { label: name.slice(0, -3), prefix: '', suffix: '%' };
  }
  return { label: name, prefix: '', suffix: '' };
}
